import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { MessagesPage } from './api';
import type { TicketsListResponse } from './types';

/**
 * Rede de segurança ao ABRIR uma conversa: a cópia guardada está atrás do
 * cartão da lista?
 *
 * O cartão vem do servidor a cada F5 e é atualizado ao vivo, então o
 * `last_message_at` dele é a referência mais fresca que o painel já tem em mãos
 * — conferir contra ele não custa requisição nenhuma (decisão do dono: nada de
 * pedir o cabeçalho ao servidor a cada clique, isso seria uma consulta nova por
 * abertura de conversa, de todos os atendentes).
 *
 * Se o cartão diz que houve mensagem depois da última que a cópia tem, a cópia
 * está velha: quem chama descarta e a conversa abre em "carregando", vindo
 * inteira do servidor. O mesmo valor de cartão nunca provoca uma segunda
 * rebusca (vide `cartaoJaRebuscado`).
 *
 * Sem cartão (conversa aberta pela busca, fora das listas carregadas) ou sem
 * cópia → não há o que comparar: fica a cargo da reconexão (`reconexao.ts`).
 */

/**
 * Folga entre os dois carimbos. O `last_message_at` às vezes é gravado alguns
 * milissegundos depois do `created_at` da mensagem (mensagem de encerramento:
 * `new Date()` no app logo após o INSERT). Buraco de verdade é de minutos ou
 * horas; 5 s separa um do outro sem rebuscar à toa.
 */
export const FOLGA_DA_COPIA_MS = 5_000;

/**
 * Carimbo mais novo entre os cartões deste ticket em todas as listas em cache,
 * e quando (relógio desta aba) a lista que o trouxe foi atualizada.
 */
function ultimaMensagemDoCartao(
  qc: QueryClient,
  ticketId: number,
): { ms: number; atualizadoEm: number } | null {
  let achado: { ms: number; atualizadoEm: number } | null = null;
  for (const query of qc.getQueryCache().findAll({ queryKey: ['tickets'] })) {
    const dados = query.state.data as InfiniteData<TicketsListResponse> | undefined;
    for (const pagina of dados?.pages ?? []) {
      for (const t of pagina?.tickets ?? []) {
        if (t.id !== ticketId) continue;
        const ms = Date.parse(t.last_message_at);
        if (Number.isNaN(ms)) continue;
        if (achado === null || ms > achado.ms) {
          achado = { ms, atualizadoEm: query.state.dataUpdatedAt };
        }
      }
    }
  }
  return achado;
}

/** Carimbo da mensagem mais nova da cópia (`pages[0]` é o bloco mais recente). */
function ultimaMensagemDaCopia(qc: QueryClient, ticketId: number): number | null {
  const copia = qc.getQueryData<InfiniteData<MessagesPage>>(['ticket-messages', ticketId]);
  let maior: number | null = null;
  for (const m of copia?.pages?.[0]?.messages ?? []) {
    const ms = Date.parse(m.created_at);
    if (!Number.isNaN(ms) && (maior === null || ms > maior)) maior = ms;
  }
  return maior;
}

/**
 * Carimbo do cartão quando ele está à frente da cópia além da folga; senão `null`.
 *
 * Duas condições:
 *   1. o cartão diz que houve mensagem depois da última que a cópia tem;
 *   2. essa informação chegou DEPOIS de a cópia ser buscada (`dataUpdatedAt`,
 *      ambos no relógio desta aba — sem desvio de relógio entre máquinas).
 * A 2 evita desconfiar de uma cópia recém-trazida do servidor: se a conversa
 * foi buscada depois de a lista saber daquele cartão, ela já reflete o
 * servidor, e o cartão à frente é diferença de gravação (ligação de voz,
 * pacote aberto), não mensagem faltando. No caso do bug, a cópia restaurada
 * carrega a hora da busca original (horas atrás) e a lista é rebuscada no F5.
 */
function cartaoAFrenteDaCopia(qc: QueryClient, ticketId: number): number | null {
  const copia = ultimaMensagemDaCopia(qc, ticketId);
  if (copia === null) return null;
  const cartao = ultimaMensagemDoCartao(qc, ticketId);
  if (cartao === null) return null;
  if (cartao.ms <= copia + FOLGA_DA_COPIA_MS) return null;
  const copiaBuscadaEm = qc.getQueryState(['ticket-messages', ticketId])?.dataUpdatedAt ?? 0;
  return cartao.atualizadoEm >= copiaBuscadaEm ? cartao.ms : null;
}

export function copiaDaConversaEstaVelha(qc: QueryClient, ticketId: number): boolean {
  return cartaoAFrenteDaCopia(qc, ticketId) !== null;
}

/**
 * Valor do cartão que já provocou um descarte, por conversa — no máximo UMA
 * rebusca por valor de `last_message_at`.
 *
 * Há casos em que o cartão fica legitimamente à frente da última mensagem
 * mesmo com a conversa em dia, e a busca do servidor não fecha a diferença:
 *   - ligação de voz no canal QR V2: a mensagem "Cliente ligando" nasce no
 *     toque e o ticket ganha `last_message_at` do FIM da chamada;
 *   - pacote do agrupador ainda aberto: o cartão anda a cada linha, a mensagem
 *     guarda o `created_at` da primeira;
 *   - relógio do PC adiantado (o cartão leva a hora local depois do envio);
 *   - tickets antigos com `last_message_at` gravado pelo eco da coexistência.
 * Sem esta memória, cada reabertura dessas conversas rebuscaria — requisição
 * que hoje não existe. Com ela, só volta a conferir quando o cartão MUDA
 * (mensagem nova de verdade). Vive na memória da página: F5 e logout zeram.
 */
const cartaoJaRebuscado = new Map<number, number>();

/**
 * Descarta a cópia da conversa se ela estiver atrás do cartão. Devolve `true`
 * quando descartou. Só mexe em cópia sem observador: chamada na abertura, antes
 * de a conversa se inscrever, ela nunca apaga o que alguém está lendo.
 */
export function descartarCopiaVelhaDaConversa(qc: QueryClient, ticketId: number): boolean {
  const cartao = cartaoAFrenteDaCopia(qc, ticketId);
  if (cartao === null) return false;
  if (cartaoJaRebuscado.get(ticketId) === cartao) return false;
  const chave = ['ticket-messages', ticketId];
  const observadores =
    qc.getQueryCache().find({ queryKey: chave, exact: true })?.getObserversCount() ?? 0;
  if (observadores > 0) return false;
  cartaoJaRebuscado.set(ticketId, cartao);
  qc.removeQueries({ queryKey: chave, exact: true });
  return true;
}

/**
 * Anota que a cópia já reflete o cartão atual: chamado quando a conversa na
 * tela recebe dados (busca ou atualização ao vivo). Sem isto, a primeira
 * reabertura depois de uma busca normal ainda rebuscava uma vez nos casos de
 * cartão sempre à frente (o "marcar como lida" mexe na lista e engana a
 * comparação de horários).
 */
export function registrarCartaoRefletido(qc: QueryClient, ticketId: number): void {
  const cartao = ultimaMensagemDoCartao(qc, ticketId);
  if (cartao !== null) cartaoJaRebuscado.set(ticketId, cartao.ms);
}

/** Zera a memória de rebuscas (testes). */
export function esquecerRebuscasDaCopia(): void {
  cartaoJaRebuscado.clear();
}
